import { Component, ElementRef, Input, input, model, OnInit, output, signal, SimpleChanges, ViewChild, OnChanges } from '@angular/core';
import { ReactiveFormsModule, FormsModule, FormControl } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { Observable, startWith, debounceTime, map } from 'rxjs';
import { ComboBoxInputData } from '../combo-input/combo-input.component';
import { MultiComboSearchBase } from './multi-combo-search.base';

interface HasId {
  id: string
}

@Component({
  selector: 'pixl-multi-combo-search',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatOptionModule,
    MatAutocompleteModule,
    MatIconModule,
    MatSelect
  ],
  templateUrl: './multi-combo-search.component.html',
  styleUrl: './multi-combo-search.component.scss'
})
export class MultiComboSearchComponent<T extends HasId> extends MultiComboSearchBase implements OnInit, OnChanges {
  private static openedInstance: MultiComboSearchBase | null = null;
  @Input() observable$!: Observable<T[]>;
  @Input() namingColumn!: keyof T; // the name of the column wich should be displayed
  withEmptyOption = input<boolean>(false); // Should '---' displayed in options list?
  externalSearch = input<boolean>(false);
  label = input<string>();
  placeHolder = input<string>('');
  onStateChange = output<T[] | null>();
  onSearchChange = output<string>();
  dataList = input<ComboBoxInputData[]>([]);
  selectedValues = model<T[]>([]); // this is a two-way-binding to a part of a from group in the parent component so this component knows which entrys are selected

  required = input<boolean>(false);
  isEmpty = signal(false);
  isLoading = signal(false);

  inputControl = new FormControl('');
  searchValue: string | null = null;
  selectControl = new FormControl<T[]>([]);
  filteredOptions!: Observable<ComboBoxInputData[]>;
  allVisibleOptions: T[] = [];

  @ViewChild('myInput') myInput!: ElementRef<HTMLInputElement>;
  @ViewChild('mySelect') mySelect!: MatSelect;

  close(): void {
    this.mySelect.close();
  }

  ngOnInit() {
    if (!this.externalSearch()) {
      this.filteredOptions = this.inputControl.valueChanges.pipe(
        startWith(this.inputControl.value as string),
        debounceTime(200),
        map(value => {
          return this._filter(value || '');
        })
      );
    }

    this.observable$.subscribe({
      next: (newResults) => {
        this.isLoading.set(false);

        const selected = this.selectedValues() ?? [];
        const combined = [...selected, ...newResults.filter(x => !selected.some(sel => this.compare(sel, x)))];
        this.allVisibleOptions = combined;
      },
      error: () => {
        this.isLoading.set(false);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.selectedValues && this.selectedValues) {
      this.selectControl.setValue(this.selectedValues(), { emitEvent: false });
    }
  }

  private compare(a: T, b: T): boolean {
    return a.id === b.id;
  }

  getDisplayValue(entry: T): string {
    const value1 = entry[this.namingColumn];

    if (value1) {
      return String(value1);
    }
    else {
      return '';
    }
  }

  private _filter(value: string): ComboBoxInputData[] {
    const filterValue = value.toLowerCase();
    const currentDataList = this.dataList();

    // Check if entry is empty
    if (filterValue === '') {
      return currentDataList;
    }

    // Check if the input value exactly matches a 'text' field in the dataList.
    // This is the case if an item has been selected from the list.
    const isExactTextMatch = currentDataList.some(item => {
      const itemTextLower = item.text.toLowerCase();
      return itemTextLower === filterValue;
    });

    if (isExactTextMatch) {
      return currentDataList;
    }

    // Otherwise (if the user is typing and it's not an exact match),
    // we filter the 'ComboBoxInputData' objects based on the input in the 'text' field.
    const filteredResults = currentDataList.filter(item => {
      const itemTextLower = item.text.toLowerCase();
      const includesMatch = itemTextLower.includes(filterValue);
      return includesMatch;
    });
    return filteredResults;
  }

  onInputChanged(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const value = inputElement.value;

    this.searchValue = value;
    this.inputControl.setValue(value);

    if (!this.mySelect.panelOpen) {
      this.onClick();
    }

    if (this.externalSearch()) {
      this.isLoading.set(true);
      this.onSearchChange.emit(value! || '');
    }
  }

  onOptionSelected(event: MatSelectChange): void {
    this.isEmpty.set(false);

    if (this.required() && !this.selectControl && !event) {
      this.isEmpty.set(true);
      return;
    }

    this.onStateChange.emit(this.selectControl.value);
  }

  public onClick() {
    if (MultiComboSearchComponent.openedInstance &&
      MultiComboSearchComponent.openedInstance !== this) {
      MultiComboSearchComponent.openedInstance.close();
    }
    MultiComboSearchComponent.openedInstance = this;
    this.mySelect.open();
  }
}
